// Shared markdown + math helpers used by ChatMessage (student chat) and
// ConversationViewer (admin conversation viewer) so both surfaces render
// assistant replies identically.

import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
// Imported once here so any surface that pulls in these shared plugins
// (ChatMessage, MarkdownContent/ConversationViewer) gets KaTeX's stylesheet
// without needing to remember to import it separately.
import 'katex/dist/katex.min.css';

// Splits on fenced code blocks (```...``` or ~~~...~~~) and inline code
// spans (`...`), keeping the code segments (odd indices) untouched so
// delimiter rewriting below never reaches inside code/regex examples.
const CODE_SEGMENT_SPLIT_REGEX = /(```[\s\S]*?```|~~~[\s\S]*?~~~|`[^`\n]*`)/g;

// While a response is still streaming in, a fenced code block may have an
// opening ``` or ~~~ with no closing fence yet - CODE_SEGMENT_SPLIT_REGEX
// only matches complete (balanced) fences, so a dangling opening fence and
// everything after it would otherwise fall through to rewriteMathDelimiters
// and get rewritten mid-stream. Find where that trailing, still-open fence
// starts (odd number of occurrences of a fence marker means the last one is
// unmatched) so the caller can leave that tail alone.
function findDanglingFenceStart(text) {
  let danglingStart = -1;
  for (const marker of ['```', '~~~']) {
    const positions = [];
    let idx = text.indexOf(marker);
    while (idx !== -1) {
      positions.push(idx);
      idx = text.indexOf(marker, idx + marker.length);
    }
    if (positions.length % 2 === 1) {
      const start = positions[positions.length - 1];
      if (start > danglingStart) danglingStart = start;
    }
  }
  return danglingStart;
}

/**
 * The model emits LaTeX using \[ ... \] (display math) and \( ... \)
 * (inline math) delimiters, as well as occasional $...$ / $$...$$.
 * remark-math only understands the dollar-sign delimiters, so normalize
 * the backslash-bracket/paren forms into $$ before handing the string
 * to react-markdown. This is a best-effort textual replace - it doesn't
 * try to parse LaTeX, it just swaps delimiters, and it skips fenced/inline
 * code so code examples (e.g. regex with literal \( \) or \[ \]) are left
 * untouched.
 */
function rewriteMathDelimiters(text) {
  return text
    // Display math: \[ ... \] -> $$ fences alone on their own lines, with
    // blank lines around them. remark-math v6 only treats a $$...$$ span as
    // a display `math` node (not inline) when the fences are isolated on
    // their own line; a same-line `$$expr$$` (the old behavior here) parses
    // as inline math, which visually shrinks the most common LLM
    // display-math shape (e.g. a single-line \[ ... \] quadratic formula).
    .replace(/\\\[([\s\S]*?)\\\]/g, (_, expr) => `\n\n$$\n${expr.trim()}\n$$\n\n`)
    // Inline math: \( ... \) -> $$ ... $$ on one line. remark-math is
    // configured with singleDollarTextMath: false (see remarkPlugins below)
    // - that setting disables single-$ inline math entirely so plain
    // currency text like "$5 and $10" isn't misread as math, which means
    // $...$ is no longer a valid inline-math delimiter for us; $$...$$
    // still renders inline when it appears mid-paragraph (no isolated
    // fences/blank lines around it), so \( \) maps to it directly.
    .replace(/\\\(([\s\S]*?)\\\)/g, (_, expr) => `$$${expr}$$`);
}

export function preprocessMathDelimiters(content) {
  if (!content || typeof content !== 'string') return content;

  // Leave a still-streaming, unterminated fenced code block untouched so
  // math-rewriting doesn't leak into it - see findDanglingFenceStart above.
  const danglingFenceStart = findDanglingFenceStart(content);
  const safeText = danglingFenceStart === -1 ? content : content.slice(0, danglingFenceStart);
  const codeTail = danglingFenceStart === -1 ? '' : content.slice(danglingFenceStart);

  const rewritten = safeText
    .split(CODE_SEGMENT_SPLIT_REGEX)
    .map((segment, i) => (i % 2 === 1 ? segment : rewriteMathDelimiters(segment)))
    .join('');

  return rewritten + codeTail;
}

// remark/rehype plugin lists shared by both markdown renderers.
// singleDollarTextMath: false stops remark-math from treating a single $
// as an inline-math delimiter, which otherwise swallows the text between
// two currency amounts (e.g. "$5 and $10") as garbled math.
// remarkGfm runs first so its syntax (tables, ~~strikethrough~~, autolinks,
// task lists) is registered before remarkMath's $$ handling - they parse
// disjoint syntax (pipe tables vs. dollar fences) so order doesn't change
// what either one matches, but this is the order react-markdown's own docs
// use when combining the two, so it's kept consistent here.
export const remarkPlugins = [remarkGfm, [remarkMath, { singleDollarTextMath: false }]];
export const rehypePlugins = [rehypeKatex];
